import { useCallback, useMemo, useState } from 'react';
import { createDateTimeFieldNames, DateTimeService, lt, showSuccessAlert, useTimezone } from '@mews/framework';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { WithServiceId } from '@mews-commander/types';
import { useLocalizeDateTime } from '@mews-commander/ui';
import { getCurrentEnterpriseReservationsByIds } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useResourceCategoriesByServiceId } from '@mews-commander/legacy-entities/src/utils/useResourceCategory';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    ReservationChangeValidationState,
    useValidateReservationChanges,
    ValidateReservationChangesResult,
} from '../../../utils/useValidateReservationChanges';
import { useIsReservationExceededMaxLength } from '../../../utils/useIsReservationExceededMaxLength';
import { useServiceAvailabilityQueries } from '../../../../availability';
import { IntervalAdjustmentFormKeys, IntervalAdjustmentFormValues } from './schema';
import { getUtcWithOriginalSecondsAndMilliseconds } from './utils';

const { time: rawArrivalTime } = createDateTimeFieldNames(IntervalAdjustmentFormKeys.Arrival);
const { time: rawDepartureTime } = createDateTimeFieldNames(IntervalAdjustmentFormKeys.Departure);

interface Params extends WithServiceId {
    commonInterval: { startUtc?: string; endUtc?: string };
    reservationIds: string[];
    onSuccess?: (createNewReservation: boolean) => void;
    interval: { startUtc: string; endUtc: string } | null;
}

export const useChangeInterval = ({ commonInterval, reservationIds, serviceId, onSuccess, interval }: Params) => {
    const timezone = useTimezone();
    const adjustReservationLengthMutation = useRepositoryMutation('reservations', 'updateInterval', {
        invalidateQueries: {
            services: ['getAvailabilities'],
        },
    });
    const [validationResult, setValidationResult] = useState<ValidateReservationChangesResult | null>(null);
    const reservations = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationsByIds(state, { ids: reservationIds })
    );

    const isReservationExceededMaxLength = useIsReservationExceededMaxLength(serviceId);

    const reservationExceeded = useMemo(
        () => isReservationExceededMaxLength(interval),
        [interval, isReservationExceededMaxLength]
    );

    const availabilitiesResult = useServiceAvailabilityQueries(
        serviceId,
        interval && !reservationExceeded ? [interval] : []
    );

    const validate = useValidateReservationChanges(serviceId, reservationIds);

    const resourceCategories = useResourceCategoriesByServiceId(serviceId);

    const ld = useLocalizeDateTime();

    const changeInterval = useCallback(
        (
            formValues: IntervalAdjustmentFormValues,
            dirtyFields: Partial<
                Record<keyof IntervalAdjustmentFormValues | typeof rawArrivalTime | typeof rawDepartureTime, boolean>
            >
        ) => {
            if (isReservationExceededMaxLength(interval, true)) {
                return;
            }

            let arrival = formValues[IntervalAdjustmentFormKeys.Arrival];
            let departure = formValues[IntervalAdjustmentFormKeys.Departure];

            if (!arrival || !departure) {
                return;
            }

            /**
             * this is for persisting original seconds and milliseconds for unchanged arrival/departure (bcs DatePicker will remove them)
             * @see https://mews.atlassian.net/browse/OPS-18401
             */
            if (commonInterval.startUtc && !dirtyFields[rawArrivalTime]) {
                arrival = getUtcWithOriginalSecondsAndMilliseconds(arrival, commonInterval.startUtc, timezone);
            }
            if (commonInterval.endUtc && !dirtyFields[rawDepartureTime]) {
                departure = getUtcWithOriginalSecondsAndMilliseconds(departure, commonInterval.endUtc, timezone);
            }

            const result = validate({
                omitCurrentReservations: true,
                availabilities: availabilitiesResult.flatMap((value) => value.data?.availabilities ?? []),
                availabilityBlockId: reservations?.[0]?.availabilityBlockId,
                onReject: () => {
                    setValidationResult(null);
                },
                onSubmit: () => {
                    adjustReservationLengthMutation.mutate(
                        {
                            reservationIds,
                            applyCancellationFee: formValues[IntervalAdjustmentFormKeys.ApplyCancellationFee],
                            createNewReservations: formValues[IntervalAdjustmentFormKeys.CreateNewReservation],
                            startUtc: arrival,
                            endUtc: departure,
                            rateId: formValues[IntervalAdjustmentFormKeys.CreateNewReservationRateId],
                        },
                        {
                            onSuccess: () => {
                                showSuccessAlert({ description: lt('UpdateReservationIntervalSuccess') });
                                setValidationResult(null);
                                onSuccess?.(formValues[IntervalAdjustmentFormKeys.CreateNewReservation]);
                            },
                        }
                    );
                },
                getCustomMessages: (splitAvailabilities) => {
                    const overbookedCategoryId =
                        splitAvailabilities.propertyOverbookingAvailabilities[0]?.resourceCategoryId;

                    const from = ld(DateTimeService.fromUtcString(arrival, timezone));
                    const to = ld(DateTimeService.fromUtcString(departure, timezone));

                    return {
                        [ReservationChangeValidationState.NoRightsToOverbook]: {
                            title: lt('UpdateReservationIntervalOverbookingNoRightsTitle'),
                        },
                        [ReservationChangeValidationState.UpdatingAvailabilityBlock]: {
                            description: lt('UpdateReservationIntervalUpdateAvailabilityBlockDescription', {
                                from,
                                to,
                            }),
                        },
                        [ReservationChangeValidationState.OverbookingAndUpdatingAvailabilityBlock]: {
                            description: (
                                <>
                                    {lt('UpdateReservationIntervalUpdateBlockAndOverbookDialogParagraph1', {
                                        spaceCategory:
                                            (overbookedCategoryId && resourceCategories[overbookedCategoryId]?.name) ??
                                            '',
                                    })}
                                    <br />
                                    {lt('UpdateReservationIntervalUpdateAvailabilityBlockDescription', { from, to })}
                                </>
                            ),
                        },
                    };
                },
            });
            setValidationResult(result);
        },
        [
            interval,
            commonInterval.startUtc,
            commonInterval.endUtc,
            availabilitiesResult,
            reservations,
            adjustReservationLengthMutation,
            reservationIds,
            timezone,
            resourceCategories,
            isReservationExceededMaxLength,
            validate,
            onSuccess,
            ld,
        ]
    );

    return useMemo(
        () => ({
            changeInterval,
            dialog: validationResult?.dialog,
            isPending: adjustReservationLengthMutation.isPending,
            loading: availabilitiesResult[0]?.isLoading,
        }),
        [validationResult, changeInterval, adjustReservationLengthMutation, availabilitiesResult]
    );
};
